import type {
  AiAgentSection,
  WidgetRelatedApp
} from '@inno-tools/container-bridge'
import { DEFAULT_WINDOW_SIZE } from 'shared/constants/apps'

import { WidgetMarketplace } from '../components/WidgetMarketplace/WidgetMarketplace'
import { WIDGET_MARKETPLACE_WINDOW_ID } from '../components/WidgetMarketplace/WidgetMarketplace.constants'
import { useWidgetMarketplaceFilterStore } from '../components/WidgetMarketplace/widgetMarketplaceFilterStore'
import { useWindowManager } from '../components/WindowManager/hooks/useWindowManager'
import { useSidebarWidgetsStore } from '../stores'
import { useRefsStore } from '../stores/refsStore'

interface OpenWidgetMarketplaceOptions {
  relatedApp?: WidgetRelatedApp | null
  section?: AiAgentSection | null
}

export const useOpenWindowMarketplace = () => {
  const { minimizeAllWindows, openComponentAsWindow } = useWindowManager()
  const sidebarWidgetsRef = useRefsStore(state => state.sidebarWidgetsRef)
  const widgetsPlacement = useSidebarWidgetsStore(state => state.placement)

  return (options: OpenWidgetMarketplaceOptions = {}) => {
    const rect = sidebarWidgetsRef?.getBoundingClientRect()
    const windowWidth = DEFAULT_WINDOW_SIZE.width

    const viewportWidth = window.innerWidth
    const maxX = Math.max(0, viewportWidth - windowWidth)

    let x: number
    if (widgetsPlacement === 'right') {
      const sidebarLeft = rect?.left ?? viewportWidth
      const desired = sidebarLeft - windowWidth - 20
      x = Math.max(0, Math.min(desired, maxX))
    } else {
      const sidebarRight = (rect?.left ?? 0) + (rect?.width ?? 0)
      const desired = sidebarRight
      x = Math.max(0, Math.min(desired, maxX)) + 20
    }

    const y = (rect?.top ?? 0) + 40

    const restoreWindows = minimizeAllWindows()

    // push before opening so the component's mount effect picks them up
    // — skip undefined because the listener often pushes its own value
    if (options.relatedApp !== undefined) {
      useWidgetMarketplaceFilterStore
        .getState()
        .setPendingRelatedApp(options.relatedApp)
    }
    if (options.section !== undefined) {
      useWidgetMarketplaceFilterStore
        .getState()
        .setPendingSection(options.section)
    }

    openComponentAsWindow({
      id: WIDGET_MARKETPLACE_WINDOW_ID,
      name: 'Widget Marketplace',
      label: 'Widget Marketplace',
      color: '#000',
      localComponent: () => (
        <WidgetMarketplace initialRelatedApp={options.relatedApp} />
      ),
      canMaximize: false,
      canMinimize: false,
      canSnap: false,
      position: { x, y },
      onCloseCallback: restoreWindows
    })
  }
}
